package uk.co.nickthecoder.kyd.json

import uk.co.nickthecoder.glok.property.Property
import uk.co.nickthecoder.glok.property.boilerplate.BooleanProperty
import uk.co.nickthecoder.glok.property.boilerplate.FloatProperty
import uk.co.nickthecoder.glok.property.boilerplate.IntProperty
import uk.co.nickthecoder.glok.property.boilerplate.StringProperty
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kojak.JsonArray
import uk.co.nickthecoder.kojak.JsonObject
import uk.co.nickthecoder.kojak.JsonString
import uk.co.nickthecoder.kyd.*
import uk.co.nickthecoder.kyd.appearance.*
import uk.co.nickthecoder.kyd.shapes.*
import uk.co.nickthecoder.kyd.util.Vector2

open class ActFileVersion1(val game: Game) : ActFileVersion {

    override val version = "1"


    // region == Act (public) ==

    override fun actFromJson(jAct: JsonObject): Act {
        return Act().apply {
            title = jAct.getString("title", "")
            backgroundColor = jAct.getColor("backgroundColor", Color.BLACK)
            directorType = jAct.getString("director", NoDirectorClassName)

            // Behaviour's Custom Properties
            (jAct.get("directorProperties") as? JsonObject)?.let { jProperties ->
                customPropertiesFromJson(director, jProperties)
            }

            // Stages
            for (description in game.stageDescriptions) {
                stages.add(game.createStage(description.stageName))
            }
            for (jStage in jAct.getObjects("stages")) {
                val stage = stageFromJson(jStage)
                val index: Int = stages.indexOfFirst { it.name == stage.name }
                if (index >= 0) {
                    stages[index] = stage
                } else {
                    stages.add(stage)
                }
            }
        }
    }

    override fun Act.toJson(): JsonObject {
        return JsonObject().apply {
            add("version", version)

            add("title", title)
            add("backgroundColor", backgroundColor.toString())
            addSkippingDefault("director", directorType, NoDirectorClassName)

            val directorProperties = director.customProperties()
            if (directorProperties.isNotEmpty()) {
                add("directorProperties", director.customPropertiesToJson())
            }

            // Stages
            if (stages.isNotEmpty()) {
                val jStages = JsonArray()
                for (stage in stages) {
                    jStages.add(stage.toJson())
                }
                add("stages", jStages)
            }
        }
    }

    // endregion == Act ==

    // region == Stage ==

    protected fun stageFromJson(jStage: JsonObject): Stage {

        val name = jStage.getString("name", "unknown")
        return game.createStage(name).apply {

            // Actors
            for (jActor in jStage.getObjects("actors")) {
                val actor = actorFromJson(jActor, this)
                addActor(actor)
            }
        }

    }

    protected fun Stage.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)

            // Actors
            if (actors.isNotEmpty()) {
                val jActors = JsonArray()
                for (actor in actors) {
                    jActors.add(actor.toJson())
                }
                add("actors", jActors)
            }

        }
    }

    // endregion == Stage ==

    // region == Actor ==

    protected fun actorFromJson(jActor: JsonObject, stage: Stage): Actor {
        return Actor(stage).apply {
            id = jActor.getString("id", "")
            position = jActor.getVector2("position", Vector2.ZERO)
            scale = jActor.getVector2("scale", Vector2.ONE)
            angleDegrees = jActor.getFloat("angle", 0f)
            zOrder = jActor.getFloat("zOrder", 0f)

            role = findRole(jActor.getString("role", ""))
            val role = role

            val shapeAppearance = (jActor.get("shape") as? JsonObject)?.let {
                shapeAppearanceFromJson(it)
            }

            if (role == null) {
                val font = (jActor.get("font") as? JsonString)?.let { findFont(it.string) }
                if (font == null) {
                    if (shapeAppearance == null) {
                        appearance = findAppearance(jActor.getString("appearance", ""))
                    } else {
                        appearance = shapeAppearance
                    }
                } else {
                    appearance = Label(font, "") // Text is set later
                }

                behaviourType = jActor.getString("behaviour", "")

            } else {
                appearance = shapeAppearance ?: findAppearance(
                    role, jActor.getString("appearance", role.defaultAppearancePath)
                ).appearance()

                behaviourType = role.behaviourType
            }

            appearance.tintIfAvailable = jActor.getColor("tint", Color.WHITE)

            (appearance as? Label)?.let { label ->
                label.text = jActor.getString("text", "")
                label.alignX = jActor.getFloat("alignX", 0.5f)
                label.alignY = jActor.getFloat("alignY", 0.5f)
                label.lineHeight = jActor.getFloat("lineHeight", 1.2f)
            }

            (appearance as? StretchableAppearance)?.let { stretchable ->
                stretchable.corner0 = jActor.getVector2("corner0", stretchable.corner0)
                stretchable.corner2 = jActor.getVector2("corner2", stretchable.corner2)
            }

            behaviour = findBehaviour(behaviourType)

            // Behaviour's Custom Properties
            (jActor.get("properties") as? JsonObject)?.let { jProperties ->
                customPropertiesFromJson(behaviour, jProperties)
            }

            // TODO Actor's Children
        }
    }

    protected fun Actor.toJson(): JsonObject {
        return JsonObject().apply {
            addSkippingDefault("id", id, "")
            add("position", position)
            addSkippingDefault("scale", scale, Vector2.ONE)
            addSkippingDefault("angle", angleDegrees, 0f)
            addSkippingDefault("zOrder", zOrder, 0f)

            val role = role
            val app = appearance

            if (role == null) {

                if (app is Label) {
                    add("font", app.font.path())
                } else if (app is Resource) {
                    addSkippingDefault("appearance", app.path(), "")
                } else if (app is StretchableAppearance) {
                    addSkippingDefault("appearance", app.resourcePath, "")
                }

                addSkippingDefault("behaviour", behaviourType, role?.behaviourType ?: "")

            } else {
                add("role", role.path())
            }

            addSkippingDefault("tint", appearance.tintIfAvailable, Color.WHITE)

            if (app is Label) {
                add("text", app.text)
                addSkippingDefault("alignX", app.alignX, 0.5f)
                addSkippingDefault("alignY", app.alignY, 0.5f)
                addSkippingDefault("lineHeight", app.lineHeight, 1.2f)
            }

            if (app is StretchableAppearance) {
                add("corner0", app.corner0)
                add("corner2", app.corner2)
            }

            if (app is ShapeAppearance) {
                add("shape", app.toJson())
            }

            // Behaviour custom properties
            val behaviourProperties = behaviour.customProperties()
            if (behaviourProperties.isNotEmpty()) {
                add("properties", behaviour.customPropertiesToJson())
            }

            // TODO Children
        }
    }

    protected fun findRole(path: String): Role? {
        val result = game.find(game.resolvePath(path)) as? Role

        if (result == null && path != "") log.warn("Role not found : '$path'")

        return result
    }

    protected fun findFont(path: String): Font? {
        val result = game.find(game.resolvePath(path)) as? Font

        return if (result == null) {
            log.warn("Font not found : '$path'")
            null
        } else {
            result
        }
    }

    protected fun findAppearance(path: String): Appearance {
        //println("abs of $path = ${game.resolvePath(path)}")
        val result = game.find(game.resolvePath(path)) as? AppearanceResource

        return if (result == null) {
            log.warn("Appearance not found : '$path'")
            NoAppearance
        } else {
            result.appearance()
        }
    }

    protected fun findAppearance(resource: Resource, path: String): Appearance {
        //println("abs of $path = ${game.resolvePath(path, this)} from ${this.path()}")

        val result = game.find(game.resolvePath(path, resource)) as? AppearanceResource

        return if (result == null) {
            log.warn("Appearance not found : '$path' (from ${resource.path()})")
            NoAppearance
        } else {
            result.appearance()
        }
    }

    protected fun findBehaviour(behaviourName: String): Behaviour {
        if (behaviourName.isEmpty()) return NoBehaviour
        val behaviour = KydTypeRegistry.createBehaviour(behaviourName)
        return behaviour
    }

    // endregion == Actor ==

    // region == Shape ==

    protected fun shapeAppearanceFromJson(jShape: JsonObject): ShapeAppearance {
        val gameFile = GameFileVersion1()

        val jPolygon = jShape.get("polygon") as? JsonObject
        val jLine = jShape.get("line") as? JsonObject

        val shape = if (jPolygon == null) {
            if (jLine == null) {
                null
            } else {
                gameFile.lineFromJson(jLine)
            }
        } else {
            GameFileVersion1().polygonFromJson(jPolygon)
        } ?: Polygon(
            listOf(
                Vector2(- 10f, 10f), Vector2(10f, 10f),
                Vector2(10f, - 10f), Vector2(- 10f, - 10f)
            )
        )

        return ShapeAppearance(shape).apply {
            strokeWidth = jShape.getFloat("strokeWidth", 0f)
            strokeRatio = jShape.getFloat("strokeRatio", 0.5f)
            lineWidth = jShape.getFloat("lineWidth", 1f)
            with(GameFileVersion1()) {
                capStyle = jShape.getEnum("capStyle", CapStyle.ROUND)
                joinStyle = jShape.getEnum("joinStyle", JoinStyle.ROUND)
            }

            fill = (jShape.get("fill") as? JsonObject)?.let { gameFile.paintFromJson(it) } ?: NoPaint
            (fill as? Resolvable)?.resolveReferences(game)

            stroke = (jShape.get("stroke") as? JsonObject)?.let { gameFile.paintFromJson(it) } ?: NoPaint
            (stroke as? Resolvable)?.resolveReferences(game)
        }
    }

    protected fun ShapeAppearance.toJson() = JsonObject().apply {
        addSkippingDefault("strokeWidth", strokeWidth, 0f)
        addSkippingDefault("strokeRatio", strokeRatio, 0.5f)
        addSkippingDefault("lineWidth", lineWidth, 1f)
        addSkippingDefault("capStyle", capStyle.name, CapStyle.ROUND.name)
        addSkippingDefault("joinStyle", joinStyle.name, JoinStyle.ROUND.name)

        with(GameFileVersion1()) {
            add("fill", fill.toJson())
            add("stroke", stroke.toJson())

            val shape = shape
            when (shape) {
                is Line -> {
                    add("line", shape.toJson())
                }

                is Polygon -> {
                    add("polygon", shape.toJson())
                }
            }
        }
    }

    // endregion Shape

    // region == Custom Properties ==

    protected fun customPropertiesFromJson(customisable: Customisable, jProperties: JsonObject) {
        val properties = customisable.customProperties()
        for (property in properties) {
            val name = property.beanName ?: continue
            when (property) {
                is StringProperty -> property.value = jProperties.getString(name, property.value)
                is FloatProperty -> property.value = jProperties.getFloat(name, property.value)
                is IntProperty -> property.value = jProperties.getInt(name, property.value)
                is BooleanProperty -> property.value = jProperties.getBoolean(name, property.value)
                is Vector2Property -> property.value = jProperties.getVector2(name, property.value)
                else -> {
                    val oldValue = property.value
                    if (oldValue is Enumerable<*>) {
                        val newEnumName = jProperties.getString(name, oldValue.name)
                        val newEnumValue = oldValue.enumerateValues().firstOrNull { it.name == newEnumName }
                        if (newEnumValue == null) {
                            log.warn("Enumerable value '$newEnumName' not found (custom property '$name')")
                        } else {
                            try {
                                @Suppress("UNCHECKED_CAST")
                                (property as Property<Enumerable<*>>).value = newEnumValue
                            } catch (e: Exception) {
                                log.error("Failed to set Enumerable '$newEnumValue' (custom property '$name')")
                            }
                        }
                    }
                }
            }
        }
        for (name in jProperties.names()) {
            if (properties.firstOrNull { it.beanName == name } == null) {
                log.warn("Property : '$name' not found in $customisable")
            }
        }
    }

    protected fun Customisable.customPropertiesToJson(): JsonObject {
        val properties = customProperties()
        val jAttributes = JsonObject().apply {
            for (property in properties) {
                val name = property.beanName ?: continue
                when (property) {
                    is StringProperty -> add(name, property.value)
                    is FloatProperty -> add(name, property.value)
                    is IntProperty -> add(name, property.value)
                    is BooleanProperty -> add(name, property.value)
                    is Vector2Property -> add(name, property.value)
                    else -> {
                        val value = property.value
                        if (value is Enumerable<*>) {
                            add(name, value.name)
                        }
                    }
                }
            }
        }
        return jAttributes
    }

    // endregion

    // region == Vector2 ==

    protected fun JsonObject.getVector2(name: String, defaultValue: Vector2): Vector2 {
        val x = getFloat("${name}X", defaultValue.x)
        val y = getFloat("${name}Y", defaultValue.y)
        return Vector2(x, y)
    }

    protected fun JsonObject.add(name: String, value: Vector2) {
        add("${name}X", value.x)
        add("${name}Y", value.y)
    }


    // The same as above, but for Ints.
    protected fun JsonObject.addSkippingDefault(name: String, value: Vector2, defaultValue: Vector2) {
        if (value != defaultValue) {
            add(name, value)
        }
    }
    // endregion == Vector2 ==

}

private fun actFileVersion(game: Game, versionString: String): ActFileVersion {
    return when (versionString) {
        "1" -> ActFileVersion1(game)
        else -> {
            log.warn("Version '$versionString' not known. Trying '1'")
            ActFileVersion1(game)
        }
    }
}

private fun actFileVersion(game: Game, jAct: JsonObject): ActFileVersion =
    actFileVersion(game, jAct.getString("version", "?"))

fun createJson(game: Game, act: Act): JsonObject = with(ActFileVersion1(game)) {
    act.toJson()
}

fun createActFromJson(game: Game, jAct: JsonObject): Act {

    with(actFileVersion(game, jAct)) {
        return actFromJson(jAct)
    }

}
